import { Settings2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { BrowseColumnsSlider } from '@/components/discovery/browse-columns-slider'
import { BrowseModeToggle } from '@/components/discovery/browse-mode-toggle'
import { BrowseSortSelect } from '@/components/discovery/browse-sort-select'
import type { BrowseMode } from '@/features/discovery/use-browse-mode'
import {
  ALL_TILE_SIZES,
  TILE_SIZE_LABEL,
  type TileSize,
} from '@/features/discovery/use-browse-tile-sizes'

interface BrowseViewSettingsProps {
  mode: BrowseMode
  onModeChange: (next: BrowseMode) => void
  columns: number
  onColumnsChange: (next: number) => void
  enabledSizes: Set<TileSize>
  onToggleSize: (size: TileSize) => void
}

/**
 * Single settings popover for the Browse page. Collapses sort + mode + inspiration controls
 * behind one icon button so the sticky header is just the search bar plus this trigger. The
 * popover content adapts: density + tile-size sections only render when mode is
 * {@code inspiration} (they're irrelevant for List + Card).
 */
export const BrowseViewSettings = ({
  mode,
  onModeChange,
  columns,
  onColumnsChange,
  enabledSizes,
  onToggleSize,
}: BrowseViewSettingsProps) => (
  <Popover>
    <PopoverTrigger asChild>
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-11 w-11"
        aria-label="View settings"
      >
        <Settings2 className="h-5 w-5" />
      </Button>
    </PopoverTrigger>
    <PopoverContent className="w-72 space-y-4" align="end">
      <section>
        <div className="mb-2 text-xs font-medium text-muted-foreground">Layout</div>
        <BrowseModeToggle mode={mode} onChange={onModeChange} />
      </section>
      <section>
        <div className="mb-2 text-xs font-medium text-muted-foreground">Sort</div>
        <BrowseSortSelect />
      </section>
      {mode === 'inspiration' && (
        <>
          <section>
            <div className="mb-2 text-xs font-medium text-muted-foreground">Density</div>
            <BrowseColumnsSlider columns={columns} onChange={onColumnsChange} />
          </section>
          <section>
            <div className="mb-2 text-xs font-medium text-muted-foreground">Tile sizes</div>
            <ul className="space-y-1.5">
              {ALL_TILE_SIZES.map((size) => {
                const id = `tile-size-${size}`
                return (
                  <li key={size} className="flex items-center gap-2">
                    <Checkbox
                      id={id}
                      checked={enabledSizes.has(size)}
                      onCheckedChange={() => onToggleSize(size)}
                    />
                    <label htmlFor={id} className="cursor-pointer text-sm">
                      {TILE_SIZE_LABEL[size]}
                    </label>
                  </li>
                )
              })}
            </ul>
          </section>
        </>
      )}
    </PopoverContent>
  </Popover>
)
